import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Select } from 'antd';

import FormBuilder from '../../forms/FormBuilder/FormBuilder';
import CompanySettingsCard from '../CompanySettingsCard';
import SettingsCardHeader from '../SettingsCardHeader';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { getCostcodeCustomFieldTemplate, updateCostcodeCustomFieldTemplate } from '../../costcodes/state/costcodes.actions';
import {
  getUserCustomFieldTemplate,
  updateUserCustomFieldTemplate,
} from '../../users/state/users.actions';
import { getCustomFields } from '../../timecards/state/timecards.actions';

import {
  getCertificationCustomFieldTemplate,
  updateCertificationCustomFieldTemplate,
} from '../../certifications/state/certifications.actions';
import { getProjectCustomFieldTemplate, updateProjectCustomFieldTemplate } from '../../projects/state/projects.actions';
import { getCustomerCustomFieldTemplate, updateCustomerCustomFieldTemplate } from '../../contacts/customers/state/customers.actions';

const TYPES_OPTIONS = [
  { value: 'costcodes', label: 'Cost Codes' },
  { value: 'users', label: 'Users' },
  { value: 'usercertifications', label: 'User Certifications' },
  { value: 'vendorcertifications', label: 'Vendor Certifications' },
  { value: 'equipmentcertifications', label: 'Equipment Certifications' },
  { value: 'projects', label: 'Projects' },
  { value: 'customers', label: 'Customers' },
];

export default function CustomFields() {
  const dispatch = useDispatch();
  const costcodeCustomFields = useSelector((state) => state.costcodes.customFieldTemplate);
  const userCustomFields = useSelector((state) => state.users.customFieldTemplate);
  const projectCustomFields = useSelector((state) => state.projects.customFieldTemplate);
  const customerCustomFields = useSelector((state) => state.customers.customFieldTemplate);
  const company = useSelector((state) => state.settings.company);
  const {
    users: {
      certificationCustomFieldTemplate: userCertificationCustomFields,
    } = {},
    vendors: {
      certificationCustomFieldTemplate: vendorCertificationCustomFields,
    } = {},
    equipment: {
      certificationCustomFieldTemplate: equipmentCertificationCustomFields,
    } = {},
  } = useSelector(
    (state) => state.certifications,
  );

  const [sections, setSections] = useState([]);
  const [type, setType] = useState(TYPES_OPTIONS[0].value);

  const {
    sections: customFields,
  } = useMemo(() => {
    switch (type) {
      case 'costcodes':
        return costcodeCustomFields;
      case 'users':
        return userCustomFields;
      case 'usercertifications':
        return userCertificationCustomFields;
      case 'vendorcertifications':
        return vendorCertificationCustomFields;
      case 'equipmentcertifications':
        return equipmentCertificationCustomFields;
      case 'projects':
        return projectCustomFields;
      case 'customers':
        return customerCustomFields;
      default:
        return costcodeCustomFields;
    }
  }, [
    type,
    costcodeCustomFields,
    userCustomFields,
    userCertificationCustomFields,
    vendorCertificationCustomFields,
    equipmentCertificationCustomFields,
    projectCustomFields,
    customerCustomFields,
  ]);

  useEffect(() => {
    if (type === 'users') dispatch(getUserCustomFieldTemplate());
    if (type === 'costcodes') dispatch(getCostcodeCustomFieldTemplate());
    if (type === 'usercertifications') dispatch(getCertificationCustomFieldTemplate('users'));
    if (type === 'vendorcertifications') dispatch(getCertificationCustomFieldTemplate('vendors'));
    if (type === 'equipmentcertifications') dispatch(getCertificationCustomFieldTemplate('equipment'));
    if (type === 'projects') dispatch(getProjectCustomFieldTemplate());
    if (type === 'customers') dispatch(getCustomerCustomFieldTemplate());
  }, [type]);

  useEffect(() => {
    dispatch(getCustomFields());
  }, []);

  const onSaveFields = () => {
    if (type === 'users') dispatch(updateUserCustomFieldTemplate({ sections }));
    if (type === 'costcodes') dispatch(updateCostcodeCustomFieldTemplate({ sections }));
    if (type === 'usercertifications') dispatch(updateCertificationCustomFieldTemplate('users', { sections }));
    if (type === 'vendorcertifications') dispatch(updateCertificationCustomFieldTemplate('vendors', { sections }));
    if (type === 'equipmentcertifications') dispatch(updateCertificationCustomFieldTemplate('equipment', { sections }));
    if (type === 'projects') dispatch(updateProjectCustomFieldTemplate({ sections }));
    if (type === 'customers') dispatch(updateCustomerCustomFieldTemplate({ sections }));
  };

  const TypeSelector = useMemo(() => (
    <Select
      style={{ width: 350 }}
      value={type}
      onSelect={setType}
      options={TYPES_OPTIONS}
    />
  ), [type, TYPES_OPTIONS]);

  const hasRedesign = company?.settings?.uxRedesignPt1;

  return (
    <Row
      id="divisions-container"
      style={{
        width: '100%',
        height: '95%',
        marginLeft: 10,
        marginTop: 10,
        marginBottom: 20,
        position: 'relative',
      }}
    >
      <SettingsCardHeader
        title={TypeSelector}
        style={{ width: '100%', paddingRight: 50 }}
      >
        <OnTraccrButton
          title="Save"
          type="primary"
          onClick={onSaveFields}
        />
      </SettingsCardHeader>
      <CompanySettingsCard
        title=""
        style={{
          position: 'absolute',
          top: hasRedesign ? 50 : 30,
          bottom: 20,
          right: 50,
          left: 0,
          padding: 0,
          height: 'auto',
        }}
        containerStyle={{
          paddingLeft: 5,
          paddingRight: 5,
          width: '100%',
          border: 'none',
          height: 'inherit',
          maxHeight: 'none',
          overflowY: 'visible',
        }}
      >
        <div
          className="form-builder-container"
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
        >
          <FormBuilder
            key={type}
            isBoardCards
            style={{ inset: 0 }}
            onSectionsChanged={setSections}
            initialSections={customFields}
            keyboard={false}
            showParentAttributeCheckbox={type === 'projects'}
            allowRepeatingSections={false}
          />
        </div>
      </CompanySettingsCard>
    </Row>
  );
}
